import { useSyncExternalStore } from 'react';
import { Toaster as Sonner, type ToasterProps } from 'sonner';

import {
  applyResolvedTheme,
  getPrefs,
  resolveMode,
  subscribe as subscribeTheme,
} from '@/ui/theme/theme-store.js';

/**
 * Sonner toast portal — wired to our theme store so it picks up the same
 * light/dark/high-contrast resolution as the rest of the app.
 *
 * Mount once at the root (see `app/providers.tsx` / `boot.tsx`).
 */
export function Toaster(props: ToasterProps): React.ReactNode {
  // Re-render when the user changes themes so Sonner picks the right palette.
  const prefs = useSyncExternalStore(subscribeTheme, getPrefs, getPrefs);
  const theme = resolveMode(prefs);

  // Ensure the data-theme attribute is current before Sonner reads it.
  applyResolvedTheme();

  const dark = theme === 'dark';

  return (
    <Sonner
      theme={theme as ToasterProps['theme']}
      className="toaster group"
      position="top-center"
      richColors
      closeButton
      gap={10}
      toastOptions={{
        classNames: {
          toast:
            'group-[.toaster]:rounded-lg group-[.toaster]:shadow-md group-[.toaster]:border-0 group-[.toaster]:border-l-[3.5px] group-[.toaster]:backdrop-blur-xl group-[.toaster]:text-compact group-[.toaster]:font-medium group-[.toaster]:px-5 group-[.toaster]:py-4 group-[.toaster]:pr-10',
          title:
            'group-[.toaster]:font-bold group-[.toaster]:text-sm group-[.toaster]:leading-snug',
          description:
            'group-[.toaster]:text-caption group-[.toaster]:opacity-65 group-[.toaster]:mt-1 group-[.toaster]:leading-relaxed',
          closeButton:
            'group-[.toaster]:!left-auto group-[.toaster]:!right-2.5 group-[.toaster]:!top-2.5 group-[.toaster]:!translate-x-0 group-[.toaster]:!translate-y-0 group-[.toaster]:size-6 group-[.toaster]:rounded-lg group-[.toaster]:border-0 group-[.toaster]:bg-transparent group-[.toaster]:opacity-40 group-[.toaster]:hover:opacity-100 group-[.toaster]:transition-opacity',
          actionButton:
            'group-[.toaster]:rounded-lg group-[.toaster]:text-caption group-[.toaster]:font-medium',
        },
      }}
      style={
        {
          '--normal-bg': 'var(--popover)',
          '--normal-text': 'var(--popover-foreground)',
          '--normal-border': 'var(--border)',
          '--width': '460px',
          // Success — soft green with green left border
          '--success-bg': dark ? 'oklch(0.22 0.06 145)' : '#ecfdf3',
          '--success-border': dark ? 'oklch(0.34 0.08 145)' : '#34d399',
          '--success-text': dark ? 'oklch(0.84 0.10 145)' : '#065f46',
          // Error — soft red with red left border
          '--error-bg': dark ? 'oklch(0.22 0.06 20)' : '#fef2f2',
          '--error-border': dark ? 'oklch(0.34 0.09 20)' : '#f87171',
          '--error-text': dark ? 'oklch(0.84 0.12 20)' : '#991b1b',
          // Warning — soft amber with amber left border
          '--warning-bg': dark ? 'oklch(0.22 0.07 80)' : '#fffbeb',
          '--warning-border': dark ? 'oklch(0.35 0.09 80)' : '#fbbf24',
          '--warning-text': dark ? 'oklch(0.84 0.10 80)' : '#92400e',
          // Info — neutral gray (matches our palette, no blue branding)
          '--info-bg': dark ? 'oklch(0.25 0 0)' : '#f3f3f3',
          '--info-border': dark ? 'oklch(0.40 0 0)' : '#c8c8c8',
          '--info-text': dark ? 'oklch(0.85 0 0)' : '#3a3a3a',
        } as React.CSSProperties
      }
      {...props}
    />
  );
}
